/** The slice of the signed-in user that Sentry's scope is built from. Structural, so this module
 *  stays free of the domain `User` class and is trivially testable. */
export interface SentryScopeUser {
	id: string;
	email: string;
	fullName: string;
	roles: readonly string[];
	companyIds: readonly string[];
}

export interface SentryUserScope {
	/** Who an event is attributed to: the person at the keyboard, never the impersonation target. */
	user: { id?: string; email: string; username?: string };
	/** Searchable, low-cardinality tags. `impersonated_user` is `undefined` (cleared) when not impersonating. */
	tags: Record<string, string | undefined>;
	/** The full role/company lists, which do not fit a single tag value. */
	context: { roles: string[]; companyIds: string[] };
}

/** Collapse the user's roles to the one that best explains what they can see. */
export function primaryRole(roles: readonly string[]): "developer" | "admin" | "staff" | "customer" {
	if (roles.includes("Developer")) return "developer";
	if (roles.includes("App admin")) return "admin";
	if (roles.some((role) => role !== "Customer")) return "staff";
	return "customer";
}

/** A single company id, or `none`/`multiple` so the tag stays filterable for multi-company users. */
function companyTag(companyIds: readonly string[]): string {
	if (companyIds.length === 0) return "none";
	if (companyIds.length === 1) return companyIds[0];
	return "multiple";
}

/** The `sub` claim (the login email) of a JWT, or undefined if the token is not a readable JWT.
 *  Only decodes; the signature is the API's business. */
export function tokenSubject(jwt: string | null | undefined): string | undefined {
	const payload = jwt?.split(".")[1];
	if (!payload) return undefined;
	try {
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		const sub = JSON.parse(json)?.sub;
		return typeof sub === "string" && sub ? sub : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Build the Sentry user, tags and context for the resolved portal user.
 *
 * While a developer impersonates someone, `user` is the impersonated account (that is what
 * `/auth/me` returns), but the error was triggered by the developer. The event is attributed to
 * the token's own identity, matching the API's `set_user`, and the target is kept as the
 * `impersonated_user` tag. Role and company tags describe the account being viewed, since that
 * is what decides what rendered.
 */
export function sentryUserScope(
	user: SentryScopeUser,
	impersonatedEmail: string | null,
	jwt: string | null | undefined,
): SentryUserScope {
	const tags: Record<string, string | undefined> = {
		role: primaryRole(user.roles),
		company: companyTag(user.companyIds),
		company_count: String(user.companyIds.length),
		impersonating: impersonatedEmail ? "true" : "false",
		impersonated_user: impersonatedEmail ?? undefined,
	};
	const context = { roles: [...user.roles], companyIds: [...user.companyIds] };
	const actor = impersonatedEmail ? tokenSubject(jwt) : undefined;
	const sentryUser = actor ? { email: actor } : { id: user.id, email: user.email, username: user.fullName };
	return { user: sentryUser, tags, context };
}
